"use client";

import { useLocale, useTranslations } from "next-intl";

import Link from "next/link";
import PartnerMap from "./PartnerMap";
import { RetrievedPartner } from "@/lib/sanity/responseTypes";

import PhoneIcon from "@/components/icons/phone.svg";
import EnvelopeIcon from "@/components/icons/envelope-outline.svg";
import TextIcon from "@/components/icons/bars-3-bottom-left-white.svg";
import ContactForm from "./ContactForm";

interface ContactFrontPageViewProps {
  partners: RetrievedPartner[];
}

function ContactFrontPageView(props: ContactFrontPageViewProps) {
  const t = useTranslations('Home');
  const locale = useLocale();

  return (
    <div id="kontakt" className="w-full relative overflow-hidden flex flex-col items-start md:py-[30px] md:px-[120px] px-5 py-5 box-border text-left text-base font-[Outfit] scroll-mt-[60px]">
      <div className="self-stretch overflow-hidden shrink-0 flex md:flex-row flex-col justify-start content-center gap-2.5">
        <div className="w-full flex-1 overflow-hidden flex flex-col items-start justify-center gap-2.5">
          <div className="w-full self-stretch relative text-2xl font-bold leading-[120%] inline-block shrink-0 text-wrap pb-3.5">
            {t('contact_title')}
          </div>
          <div className="w-full relative text-[20px] font-bold leading-[120%] flex items-center shirt-0 h-[36px]">
            {t('contact_welcome')}
          </div>
          <Link href="tel:+41617039244" className="w-full overflow-hidden flex items-center gap-2.5 text-base">
            <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center bg-secondary shadow-[0_6px_4px_rgba(0,0,0,0.25)]">
              <PhoneIcon className="h-6 w-full relative stroke-white" />
            </div>
            <div className="relative overflow-hidden shrink-0 flex flex-col items-start p-2.5 gap-0 box-border text-left text-base">
              <div className="relative font-bold leading-[120%]">
                {t('contact_phone')}
              </div>
              <div className="leading-[120%]">
                {"+41 61 703 92 44"}
              </div>
            </div>
          </Link>
          <Link href="mailto:info@invento.ch" className="w-full overflow-hidden flex items-center gap-2.5 text-base">
            <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center bg-secondary p-2.5 shadow-[0_6px_4px_rgba(0,0,0,0.25)]">
              <EnvelopeIcon className="h-6 w-full relative stroke-white" />
            </div>
            <div className="relative overflow-hidden shrink-0 flex flex-col items-start p-2.5 gap-0 box-border text-left text-base">
              <div className="relative font-bold leading-[120%]">
                {t('contact_email')}
              </div>
              <div className="leading-[120%]">
                {"info@invento.ch"}
              </div>
            </div>
          </Link>
          <div className="w-full overflow-hidden flex items-center gap-2.5 text-base cursor-pointer" onClick={() => (document.getElementById('contact_modal') as HTMLDialogElement).showModal()}>
            <div className="w-12 h-12 rounded-full overflow-hidden flex items-center justify-center bg-secondary p-2.5 shadow-[0_6px_4px_rgba(0,0,0,0.25)]">
              <TextIcon className="h-6 w-full relative stroke-white" />
            </div>
            <div className="relative overflow-hidden shrink-0 flex flex-col items-start p-2.5 gap-0 box-border text-left text-base">
              <div className="relative font-bold leading-[120%]">
                {t('contact_contactform')}
              </div>
              <div className="leading-[120%]">
                {t('contact_contactform_sub')}
              </div>
            </div>
          </div>
          <div className="w-full relative text-[20px] font-bold leading-[120%] shirt-0 h-12 pt-6">
            {t('contact_openinghours')}
          </div>
          <div className="w-full h-[92px] relative overflow-hidden shrink-0 grid justify-start align-start gap-0 text-left text-base">
            <div className="absolute top-0 left-0 leading-[120%] inline-block w-[169px] h-7">{t('contact_mo_do')}</div>
            <div className="absolute top-0 left-[190px] leading-[120%] inline-block w-[158px] h-[38px]">
              {"07:30 - 12:00"}<br />{"13:00 - 16:30"}
            </div>
            <div className="absolute top-[51px] left-0 leading-[120%] inline-block w-[169px] h-7">{t('contact_fr')}</div>
            <div className="absolute top-[51px] left-[190px] leading-[120%] inline-block w-[158px] h-[38px]">
              {"07:30 - 12:00"}<br />{"13:00 - 16:00"}
            </div>
          </div>
        </div>
        <div className="flex-1 flex flex-row overflow-hidden box-border h-80 md:h-auto w-full">
          <PartnerMap partners={props.partners} />
        </div>
        <dialog id="contact_modal" className="modal">
          <div className="modal-box w-11/12 xl:w-8/12 max-w-5xl">
            <h3 className="font-bold text-lg font-[Outfit]">{t('contact_contactform')}</h3>
            <form method="dialog">
              {/* if there is a button in form, it will close the modal */}
              <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">✕</button>
            </form>
            <ContactForm submitPath="/api/contact" currentPage={`${process.env.NEXT_PUBLIC_BASE_URL}/${locale}`} />
          </div>
        </dialog>
      </div>
    </div>
  )
}

export default ContactFrontPageView;
